import { useCallback, useLayoutEffect, useState } from 'react';

/**
 * Max height for a frame that scrolls on its own (a table with a sticky header)
 * so that the page around it does not scroll too — two scrollbars side by side
 * otherwise. A fixed `max-h-[calc(100vh-Npx)]` cannot do it: N is whatever sits
 * above and below the frame (header, filters, banners, pagination, footer), and
 * that changes with the screen width and with what is shown.
 *
 *   frameRef — the scrolling frame
 *   pageRef  — the page root; what lies between the frame and its bottom
 *              (pagination, card padding) stays under the frame
 * The footer space is `.ptl-content`'s padding-bottom (MainLayout's footer is
 * absolutely positioned inside it).
 *
 * Returns null when disabled — the frame then grows and the page scrolls.
 */
export const fitHeight = ({ viewport, top, below, reserved, min }) =>
  Math.max(min, Math.floor(viewport - top - below - reserved));

const useFitToViewport = (frameRef, pageRef, { enabled = true, minHeight = 240, deps = [] } = {}) => {
  const [maxHeight, setMaxHeight] = useState(null);

  const measure = useCallback(() => {
    const frame = frameRef.current;
    const page = pageRef.current;
    if (!enabled || !frame || !page) {
      setMaxHeight(null);
      return;
    }

    const frameRect = frame.getBoundingClientRect();
    const content = page.closest('.ptl-content');
    setMaxHeight(fitHeight({
      viewport: window.innerHeight,
      top:      frameRect.top + window.scrollY,
      below:    Math.max(0, page.getBoundingClientRect().bottom - frameRect.bottom),
      reserved: content ? parseFloat(window.getComputedStyle(content).paddingBottom) || 0 : 0,
      min:      minHeight,
    }));
  }, [frameRef, pageRef, enabled, minHeight]);

  // deps: whatever changes the height above / below the frame (filter chips,
  // a count line that appears, pagination shown or not).
  useLayoutEffect(measure, [measure, ...deps]);

  useLayoutEffect(() => {
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [measure]);

  return maxHeight;
};

export default useFitToViewport;
